import { useAuth, useClerk, useOrganization, useOrganizationList } from "@clerk/react";
import { PlusIcon, UploadIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";

import { readCompanyLogoFile } from "../../onboardingStore";
import {
  organizationLogoUrl,
  resolveOrganizationLogoUrl,
} from "../onboarding/MorrowOnboarding.logic";
import { syncOrgSeats } from "../../orgMembers";
import { RenderErrorBoundary } from "../RenderErrorBoundary";
import { Button } from "../ui/button";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Spinner } from "../ui/spinner";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "../settings/settingsLayout";
import { searchableSetting } from "../settings/settingsSearch";
import { useI18n } from "~/i18n";
import { OrganizationSettingsDisabledHint } from "./organizationMembersPanelView";
import { OrganizationMembersPanel } from "./OrganizationMembersPanel";
import {
  OrganizationAvatar,
  PersonalAccountAvatar,
  useCompanyLogoFallback,
} from "./organizationShared";

/** Personal-account sentinel for the switch <Select> (Clerk uses `null`). */
const PERSONAL_VALUE = "__personal__";

/**
 * Settings surface for organization + member management. Uses our own settings
 * layout for org identity and member controls instead of Clerk's nested
 * OrganizationProfile card.
 *
 * Degrades gracefully at every rung:
 *   - unconfigured cloud / signed-out  → nothing to manage, show a hint
 *   - no active organization           → guide the user to create/select one
 *   - Organizations disabled on Clerk  → error boundary shows a dashboard hint
 */
export function OrganizationSettings() {
  return (
    <SettingsPageContainer>
      <OrganizationSettingsBody />
    </SettingsPageContainer>
  );
}

function OrganizationSettingsBody() {
  const { t } = useI18n();
  if (!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY) {
    return (
      <OrganizationSettingsDisabledHint>
        {t("settings.organization.signIn")}
      </OrganizationSettingsDisabledHint>
    );
  }
  return <ConfiguredOrganizationSettings />;
}

function ConfiguredOrganizationSettings() {
  const { t } = useI18n();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();

  if (!authLoaded) return null;
  if (!isSignedIn) {
    return (
      <OrganizationSettingsDisabledHint>
        {t("settings.organization.signIn")}
      </OrganizationSettingsDisabledHint>
    );
  }

  return (
    <RenderErrorBoundary
      fallback={
        <OrganizationSettingsDisabledHint>
          {t("settings.organization.disabled")}
        </OrganizationSettingsDisabledHint>
      }
    >
      <ActiveOrganizationSettings />
    </RenderErrorBoundary>
  );
}

function ActiveOrganizationSettings() {
  const { t } = useI18n();
  const clerk = useClerk();
  const { isLoaded: orgLoaded, organization, membership } = useOrganization();
  const {
    isLoaded: listLoaded,
    setActive,
    userMemberships,
  } = useOrganizationList({
    userMemberships: true,
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoOverrideUrl, setLogoOverrideUrl] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const companyLogoFallback = useCompanyLogoFallback();
  const isOrgAdmin = membership?.role === "org:admin";

  // Keep Clerk's native seat cap (maxAllowedMemberships) in lockstep with the
  // plan whenever this page is opened, so the "N of M seats" here reflects the
  // plan — not a stale instance default. Admin-gated + best-effort server-side.
  useEffect(() => {
    if (organization) void syncOrgSeats();
  }, [organization]);

  const handleLogoPick = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.currentTarget.files?.[0] ?? null;
      event.currentTarget.value = "";
      if (!file || !organization) return;
      setLogoUploading(true);
      try {
        await organization.setLogo({ file });
        const reloaded = await organization.reload();
        const uploaded = organizationLogoUrl(reloaded);
        if (uploaded) setLogoOverrideUrl(uploaded);
      } catch {
        const preview = await readCompanyLogoFile(file);
        if (preview) setLogoOverrideUrl(preview);
      } finally {
        setLogoUploading(false);
      }
    },
    [organization],
  );

  if (!orgLoaded) return null;

  const memberships = listLoaded ? (userMemberships.data ?? []) : [];
  const orgs = memberships.map((entry) => entry.organization);
  const activeName = organization?.name?.trim() || null;
  const activeLogoUrl =
    logoOverrideUrl || resolveOrganizationLogoUrl(organization, companyLogoFallback);
  const identity = activeName ?? t("settings.organization.personal");
  const memberCount = organization?.membersCount ?? 0;
  const roleLabel =
    membership?.role === "org:admin"
      ? t("settings.organization.roleAdmin")
      : t("settings.organization.roleMember");

  if (!organization) {
    return (
      <SettingsSection
        {...searchableSetting("organization", t)}
        title={t("settings.section.organization")}
      >
        <SettingsRow
          title={t("settings.organization.personal")}
          description={t("settings.organization.personalAccount")}
        />

        {orgs.length > 0 ? (
          <SettingsRow
            title={t("settings.organization.switchTitle")}
            description={t("settings.organization.switchDescription")}
            control={
              <Select
                value={PERSONAL_VALUE}
                onValueChange={(value) => {
                  if (typeof value !== "string" || !setActive || value === PERSONAL_VALUE) return;
                  void setActive({ organization: value });
                }}
              >
                <SelectTrigger
                  className="w-full sm:w-56"
                  aria-label={t("settings.organization.switchTitle")}
                >
                  <SelectValue>{identity}</SelectValue>
                </SelectTrigger>
                <SelectPopup align="end" alignItemWithTrigger={false}>
                  <SelectItem hideIndicator value={PERSONAL_VALUE}>
                    <span className="flex items-center gap-2">
                      <PersonalAccountAvatar className="size-5" />
                      {t("settings.organization.personal")}
                    </span>
                  </SelectItem>
                  {orgs.map((org) => (
                    <SelectItem hideIndicator key={org.id} value={org.id}>
                      <span className="flex items-center gap-2">
                        <OrganizationAvatar
                          logoUrl={resolveOrganizationLogoUrl(org, companyLogoFallback)}
                          name={org.name}
                        />
                        <span className="truncate">{org.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>
            }
          />
        ) : null}

        <SettingsRow
          title={t("settings.organization.createTitle")}
          description={t("settings.organization.createDescription")}
          control={
            <Button size="xs" variant="outline" onClick={() => clerk.openCreateOrganization()}>
              <PlusIcon />
              {t("settings.organization.createAction")}
            </Button>
          }
        />
      </SettingsSection>
    );
  }

  return (
    <OrganizationSettingsLayout
      identity={identity}
      activeLogoUrl={activeLogoUrl}
      roleLabel={roleLabel}
      memberCount={memberCount}
      showSwitch={orgs.length > 0}
      showCreate
      showLogoUpload={isOrgAdmin}
      switchControl={
        <Select
          value={organization.id}
          onValueChange={(value) => {
            if (typeof value !== "string" || !setActive) return;
            void setActive({ organization: value === PERSONAL_VALUE ? null : value });
          }}
        >
          <SelectTrigger
            className="w-full sm:w-56"
            aria-label={t("settings.organization.switchTitle")}
          >
            <SelectValue>{identity}</SelectValue>
          </SelectTrigger>
          <SelectPopup align="end" alignItemWithTrigger={false}>
            <SelectItem hideIndicator value={PERSONAL_VALUE}>
              <span className="flex items-center gap-2">
                <PersonalAccountAvatar className="size-5" />
                {t("settings.organization.personal")}
              </span>
            </SelectItem>
            {orgs.map((org) => (
              <SelectItem hideIndicator key={org.id} value={org.id}>
                <span className="flex items-center gap-2">
                  <OrganizationAvatar
                    logoUrl={resolveOrganizationLogoUrl(org, companyLogoFallback)}
                    name={org.name}
                  />
                  <span className="truncate">{org.name}</span>
                </span>
              </SelectItem>
            ))}
          </SelectPopup>
        </Select>
      }
      logoControl={
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={(event) => void handleLogoPick(event)}
          />
          <Button
            size="xs"
            variant="outline"
            disabled={logoUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {logoUploading ? <Spinner /> : <UploadIcon />}
            {logoUploading
              ? t("settings.organization.uploadingLogo")
              : t("settings.organization.changeLogo")}
          </Button>
        </>
      }
      onCreateOrganization={() => clerk.openCreateOrganization()}
      membersPanel={<OrganizationMembersPanel isAdmin={isOrgAdmin} />}
    />
  );
}

function OrganizationSettingsLayout({
  identity,
  activeLogoUrl,
  roleLabel,
  memberCount,
  showSwitch,
  showCreate,
  showLogoUpload,
  switchControl,
  logoControl,
  onCreateOrganization,
  membersPanel,
}: {
  readonly identity: string;
  readonly activeLogoUrl: string;
  readonly roleLabel: string;
  readonly memberCount: number;
  readonly showSwitch: boolean;
  readonly showCreate: boolean;
  readonly showLogoUpload: boolean;
  readonly switchControl?: React.ReactNode;
  readonly logoControl?: React.ReactNode;
  readonly onCreateOrganization?: () => void;
  readonly membersPanel: React.ReactNode;
}) {
  const { t } = useI18n();
  const clerk = useClerk();

  return (
    <>
      <SettingsSection
        {...searchableSetting("organization", t)}
        title={t("settings.section.organization")}
      >
        <div className="px-3 sm:px-4">
          <div className="card-glass flex items-center gap-4 rounded-xl border border-transparent p-4">
            <OrganizationAvatar
              logoUrl={activeLogoUrl}
              name={identity}
              className="size-14 rounded-xl text-base"
            />
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-base font-semibold tracking-[-0.02em] text-foreground">
                {identity}
              </h3>
              <p className="mt-0.5 text-[13px] text-muted-foreground/80">
                {t("settings.organization.pageDescription")}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-md border border-border/60 bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                  {roleLabel}
                </span>
                {memberCount > 0 ? (
                  <span className="text-[11px] text-muted-foreground/70">
                    {t("settings.organization.memberCount", { count: memberCount })}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {showSwitch && switchControl ? (
          <SettingsRow
            title={t("settings.organization.switchTitle")}
            description={t("settings.organization.switchDescription")}
            control={switchControl}
          />
        ) : null}

        {showLogoUpload && logoControl ? (
          <SettingsRow
            title={t("settings.organization.changeLogo")}
            description={t("settings.organization.changeLogoDescription")}
            control={logoControl}
          />
        ) : null}

        {showCreate ? (
          <SettingsRow
            title={t("settings.organization.createTitle")}
            description={t("settings.organization.createDescription")}
            control={
              <Button
                size="xs"
                variant="outline"
                onClick={() => (onCreateOrganization ?? clerk.openCreateOrganization)()}
              >
                <PlusIcon />
                {t("settings.organization.createAction")}
              </Button>
            }
          />
        ) : null}
      </SettingsSection>

      <SettingsSection
        {...searchableSetting("organization-members", t)}
        id="organization-members"
        title={t("settings.organization.membersTitle")}
      >
        <p className="px-3 sm:px-4 text-[13px] leading-[1.45] text-muted-foreground/80">
          {t("settings.organization.membersDescription")}
        </p>
        {membersPanel}
      </SettingsSection>
    </>
  );
}
